import React, { useEffect, useState } from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { UpsellOfferSpecialV3 } from '../../components/UpsellOfferSpecialV3';
import { UpsellFaqV2 } from '../../components/UpsellFaqV2';
import { UpsellProgressBar } from '../../components/UpsellProgressBar';
import { PurchaseSuccessModal } from '../../components/PurchaseSuccessModal';
import liveSupportImageSrc from '../../assets/images/upsell/live-support.png';
import securityAdvisorImageSrc from '../../assets/images/upsell/security-advisor.png';
import { useUpsellStep } from '../../shared/use-upsell-flow';
import { LIVE_SUPPORT_STEP } from '../../../lib/shared/upsell-flow-steps';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { buyUpsell } from '../../../lib/use-cases/buy-upsell';
import { LIFETIME_LIVE_SUPPORT_2999_PRODUCT } from '../../../lib/shared/products';
import { UPSELL_STEPS } from '../../../lib/shared/upsell-steps';
import styles from './UpsellSpecialV3Page.css';

const BASIC_SUPPORT_ITEMS = [
    'Email response within 24-48 hours',
    'Available Monday-Friday, 9am-5pm EST only',
    'General questions only',
    'No help with security threats or scams',
    "You're on your own for urgent issues"
];

const ADVISOR_ITEMS = [
    'Instant response - under 1 minute, 24/7/365',
    'Your own advisor, always available',
    'Cover any specific security issues',
    "We'll explain exactly what happened — and what to do next",
    'Never alone when something scary happens'
];

const RISK_ITEMS = [
    'Your personal data may be leaked and sold',
    'You could lose access to important online accounts',
    'Your photos and personal information could be used by scammers',
    'Your bank account could be drained overnight',
    'Criminals could open credit accounts in your name'
];

const REVIEWS = [
    {
        name: 'Robert Wilson',
        location: 'New Zealand',
        initials: 'RW',
        avatarColor: '#3B945F',
        text: 'For me personally I appreciate the support in securing my online business from the hackers, as I have been hacked and the hackers stealing my online business information. Thank you so much for your service rendered to me.'
    },
    {
        name: 'Mary Jane',
        location: 'Canada',
        initials: 'MJ',
        avatarColor: '#735396',
        text: 'The customer service Is so special. Immediate return and response. Very kind and thoughtful. Thank you ⭐️⭐️⭐️⭐️⭐️'
    },
    {
        name: 'Mark',
        location: 'USA',
        initials: 'M',
        avatarColor: '#AE4700',
        text: 'No questions, no excuses, no hesitation, no attempts to change my mind. Just a simple “Thank you, and we’ve taken care of your request.” This is how business should be handled! Much appreciated.'
    }
];

function UpsellSpecialV3Page() {
    const { subscriptionId, nextUrl, ui, withSuccessModal } = useUpsellStep(LIVE_SUPPORT_STEP);
    const [isPending, setIsPending] = useState(false);

    useEffect(() => {
        logEvent(EVENT_TYPES.UPSELL_LIVE_SUPPORT_PAGE_VISITED);
    }, []);

    const onBuy = async () => {
        setIsPending(true);

        logEvent(EVENT_TYPES.UPSELL_PAGE_CLICK_PAY, { container: 'body', 'upsell_type': 'live_support' });

        await buyUpsell(subscriptionId, LIFETIME_LIVE_SUPPORT_2999_PRODUCT);

        window.location.replace(nextUrl);
    };

    const renderRiskSection = () => (
        <section className={styles.risk}>
            <h2 className={styles.riskTitle}>What You Might Risk Without Us</h2>
            <ul className={styles.riskList}>
                {RISK_ITEMS.map(item => (
                    <li key={item}>
                        <Icon name='warning' className={styles.riskIcon} />
                        {item}
                    </li>
                ))}
            </ul>
        </section>
    );

    return (
        <>
            {withSuccessModal && <PurchaseSuccessModal />}
            <Header
                url={window.location.pathname}
                className={styles.header}
                finished={false}
                renderLeftNavigation={() => (
                    <Button
                        label='SKIP'
                        type='secondary'
                        iconRight={{ name: 'chevronRight' }}
                        link={{ to: nextUrl }}
                        className={styles.skipButton}
                        onClick={() => logEvent(EVENT_TYPES.UPSELL_PAGE_SKIPPED, { 'target_click_block': 'header' })}
                    />
                )}
            />
            <UpsellProgressBar currentStep={UPSELL_STEPS.ADD_UPGRADE} />
            <main className={styles.main} id='upsell'>
                <div className={styles.heroAndOffer}>
                    <div className={styles.heroBlock}>
                        <section className={styles.hero}>
                            <div
                                className={styles.heroImage}
                                style={{ backgroundImage: `url(${liveSupportImageSrc})` }}
                            >
                                <div className={styles.ratingBadge}>
                                    <Icon name='starPrimary' className={styles.ratingIcon} />
                                    <span><strong>4.8/5</strong>&nbsp;member rating</span>
                                </div>
                                <span className={styles.liveBadge}>
                                    <span className={styles.liveDot} />
                                    LIVE
                                </span>
                            </div>
                        </section>
                        <div className={styles.titleBlock}>
                            <h1 className={styles.title}>
                                Get a <span>Personal Expert</span> to Fix any Online Trouble
                            </h1>
                            <p className={styles.description}>Add a security advisor to your plan — available whenever something feels off</p>
                        </div>
                    </div>
                    {ui.riskPosition === 'beforeOffer' && renderRiskSection()}
                    <UpsellOfferSpecialV3
                        isPending={isPending}
                        onBuy={onBuy}
                        skipUrl={nextUrl}
                        onSkip={() => logEvent(EVENT_TYPES.UPSELL_PAGE_SKIPPED, { 'target_click_block': 'body' })}
                    />
                </div>
                <section className={styles.comparison}>
                    <article className={styles.basicCard}>
                        <h3 className={styles.cardTitle}>Basic Support</h3>
                        <ul className={styles.comparisonList}>
                            {BASIC_SUPPORT_ITEMS.map(item => (
                                <li key={item}>
                                    <Icon name='checkCircle' className={styles.basicIcon} />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </article>
                    <p className={styles.versus}>vs.</p>
                    <article className={styles.advisorCard}>
                        <h3 className={styles.cardTitleLight}>Personal Security Advisor</h3>
                        <p className={styles.advisorIntro}>All basic support features, plus:</p>
                        <ul className={styles.comparisonList}>
                            {ADVISOR_ITEMS.map(item => (
                                <li key={item}>
                                    <Icon name='checkCircle' className={styles.advisorIcon} />
                                    {item}
                                </li>
                            ))}
                        </ul>
                        <div className={styles.advisorMiniCard}>
                            <div className={styles.advisorMiniAvatarWrapper}>
                                <div className={styles.advisorMiniAvatar}>
                                    <img src={securityAdvisorImageSrc} alt='Security Advisor' />
                                </div>
                                <span className={styles.advisorOnlineDot} />
                            </div>
                            <div>
                                <p className={styles.advisorMiniTitle}>Security Advisor</p>
                                <p className={styles.advisorMiniSubtitle}>Available 24/7 for your protection</p>
                            </div>
                        </div>
                    </article>
                </section>
                {(ui.riskPosition ?? 'afterOffer') === 'afterOffer' && renderRiskSection()}
                {ui.withReviews !== false && (
                    <section className={styles.reviews}>
                        <h2 className={styles.reviewsTitle}>What Members Say</h2>
                        <ul className={styles.reviewsList}>
                            {REVIEWS.map(({ name, location, initials, avatarColor, text }) => (
                                <li key={name} className={styles.review}>
                                    <header className={styles.reviewHeader}>
                                        <div
                                            className={styles.reviewAvatar}
                                            style={{ backgroundColor: avatarColor }}
                                            aria-hidden='true'
                                        >
                                            {initials}
                                        </div>
                                        <div className={styles.reviewIdentity}>
                                            <p className={styles.reviewName}>
                                                {name}
                                                <Icon name='verified' className={styles.verifiedIcon} />
                                            </p>
                                            <p className={styles.reviewLocation}>{location}</p>
                                        </div>
                                    </header>
                                    <p className={styles.reviewText}>{text}</p>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
                {ui.withFaq !== false && <UpsellFaqV2 />}
                <UpsellOfferSpecialV3
                    isPending={isPending}
                    onBuy={onBuy}
                    skipUrl={nextUrl}
                    onSkip={() => logEvent(EVENT_TYPES.UPSELL_PAGE_SKIPPED, { 'target_click_block': 'body_bottom' })}
                />
            </main>
            <Footer />
        </>
    );
}

export {
    UpsellSpecialV3Page
};
